// Servicio de órdenes de compra para e-commerce
// Gestión de órdenes en Supabase

import { supabase } from '../lib/supabase';

/**
 * Crea una nueva orden de compra
 * @param {Object} orden - Datos de la orden
 * @param {string} orden.user_id - ID del usuario
 * @param {string} orden.producto_id - ID del producto
 * @param {number} orden.monto - Monto de la orden
 * @param {string} orden.mp_preference_id - ID de preferencia de Mercado Pago
 * @param {string} orden.external_reference - Referencia externa
 * @returns {Promise<Object>} Orden creada
 */
export const crearOrden = async (orden) => {
  try {
    const { data, error } = await supabase
      .from('ordenes_compra')
      .insert([{
        user_id: orden.user_id,
        producto_id: orden.producto_id,
        monto: orden.monto,
        estado: 'pendiente',
        mp_preference_id: orden.mp_preference_id || null,
        external_reference: orden.external_reference || null,
        metadata: orden.metadata || {}
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creando orden:', error);
    throw error;
  }
};

/**
 * Actualiza el estado de una orden
 * @param {string} ordenId - ID de la orden
 * @param {string} nuevoEstado - Nuevo estado ('pendiente', 'aprobado', 'rechazado', 'cancelado')
 * @returns {Promise<Object>} Orden actualizada
 */
export const actualizarEstadoOrden = async (ordenId, nuevoEstado) => {
  try {
    const { data, error } = await supabase
      .from('ordenes_compra')
      .update({
        estado: nuevoEstado,
        updated_at: new Date().toISOString()
      })
      .eq('id', ordenId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error actualizando estado de orden:', error);
    throw error;
  }
};

/**
 * Obtiene todas las órdenes de un usuario
 * @param {string} userId - ID del usuario
 * @returns {Promise<Array>} Lista de órdenes del usuario
 */
export const obtenerOrdenesUsuario = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('ordenes_compra')
      .select('*, productos(*)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error obteniendo órdenes del usuario:', error);
    throw error;
  }
};

/**
 * Obtiene una orden por referencia externa
 * @param {string} externalReference - Referencia externa de Mercado Pago
 * @returns {Promise<Object>} Orden encontrada
 */
export const obtenerOrdenPorReferencia = async (externalReference) => {
  try {
    const { data, error } = await supabase
      .from('ordenes_compra')
      .select('*, productos(*)')
      .eq('external_reference', externalReference)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error obteniendo orden por referencia:', error);
    throw error;
  }
};

/**
 * Obtiene una orden por ID
 * @param {string} ordenId - ID de la orden
 * @returns {Promise<Object>} Orden encontrada
 */
export const obtenerOrdenPorId = async (ordenId) => {
  try {
    const { data, error } = await supabase
      .from('ordenes_compra')
      .select('*, productos(*)')
      .eq('id', ordenId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error obteniendo orden por ID:', error);
    throw error;
  }
};
